import { OptionBadge } from "@/components/badges";
import { Card } from "@/components/ui/card";
import { ChevronRight, Building2 } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR");

function Bar({ value, total, color }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  const bg =
    {
      green: "bg-emerald-500",
      rose: "bg-rose-500",
      blue: "bg-blue-500",
      amber: "bg-amber-500",
      slate: "bg-slate-400",
      violet: "bg-violet-500",
      teal: "bg-teal-500",
    }[color] || "bg-slate-400";
  return (
    <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
      <div className={`h-full ${bg} transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function countFor(records, key, options) {
  const map = {};
  (options || []).forEach((o) => (map[o.value] = 0));
  records.forEach((r) => {
    const v = r.values?.[key];
    if (v) map[v] = (map[v] || 0) + 1;
  });
  return map;
}

function groupSum(records, key, sumField) {
  const map = {};
  records.forEach((r) => {
    const g = (r.values?.[key] ?? "").toString().trim() || "—";
    if (!map[g]) map[g] = { count: 0, sum: 0 };
    map[g].count += 1;
    map[g].sum += parseFloat(r.values?.[sumField]) || 0;
  });
  return Object.entries(map)
    .map(([k, v]) => ({ key: k, ...v }))
    .sort((a, b) => b.sum - a.sum || b.count - a.count);
}

export default function ReportPanel({ records, columns, onFilterClick }) {
  const total = records.length;
  const colOf = (k) => columns.find((c) => c.key === k);
  const sonucCol = colOf("sonuc");
  const asamaCols = ["asama1", "asama2", "asama3", "asama4"].map(colOf).filter(Boolean);
  const branchCol = colOf("musteri_subesi");
  // il bazlı hedef + ciro toplamı
  const ilMap = {};
  records.forEach((r) => {
    const il = (r.values?.il ?? "").toString().trim();
    if (!il) return;
    if (!ilMap[il]) ilMap[il] = { il, hedef: 0, ciro: 0 };
    ilMap[il].hedef += parseFloat(r.values?.hedef) || 0;
    ilMap[il].ciro += parseFloat(r.values?.gerceklesen_ciro) || 0;
  });
  const ilAgg = Object.values(ilMap).sort((a, b) => b.hedef - a.hedef || b.ciro - a.ciro);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="report-panel">
      {sonucCol && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-sm">Sonuç Raporu</h3>
            <span className="text-xs text-muted-foreground">{total} kayıt</span>
          </div>
          <div className="space-y-3">
            {(() => {
              const map = countFor(records, "sonuc", sonucCol.options);
              return (sonucCol.options || []).map((o) => (
                <div key={o.value} data-testid={`report-sonuc-${o.value}`}>
                  <div className="flex items-center justify-between mb-1">
                    <OptionBadge value={o.value} color={o.color} />
                    <span className="text-sm font-medium">
                      {map[o.value] || 0}
                      <span className="text-muted-foreground text-xs ml-1">
                        (%{total ? Math.round(((map[o.value] || 0) / total) * 100) : 0})
                      </span>
                    </span>
                  </div>
                  <Bar value={map[o.value] || 0} total={total} color={o.color} />
                </div>
              ));
            })()}
          </div>
        </Card>
      )}

      {asamaCols.length > 0 && (
        <Card className="p-4">
          <h3 className="font-display font-semibold text-sm mb-3">Aşama Raporu</h3>
          <div className="grid grid-cols-2 gap-x-5 gap-y-3">
            {asamaCols.map((col) => {
              const map = countFor(records, col.key, col.options);
              return (
                <div key={col.key} data-testid={`report-${col.key}`}>
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                    {col.label}
                  </div>
                  <div className="space-y-1.5">
                    {(col.options || []).map((o) => (
                      <div key={o.value} className="flex items-center justify-between text-xs">
                        <OptionBadge value={o.value} color={o.color} />
                        <span className="font-medium">{map[o.value] || 0}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {branchCol && (
        <Card className="p-4" data-testid="report-branch">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-sm flex items-center gap-1.5">
              <Building2 className="w-4 h-4" /> Şube Raporu
            </h3>
            <span className="text-xs text-muted-foreground">Toplam {total} müşteri</span>
          </div>
          <div className="space-y-1.5 max-h-56 overflow-y-auto grid-scroll">
            {groupSum(records, "musteri_subesi", "gerceklesen_ciro").map((row) => (
              <button
                key={row.key}
                onClick={() => row.key !== "—" && onFilterClick?.("musteri_subesi", row.key)}
                className="w-full flex items-center justify-between text-sm py-1.5 px-2 rounded hover:bg-secondary transition-colors"
                data-testid={`branch-row-${row.key}`}
              >
                <span className="truncate">{row.key === "—" ? "Şubesiz" : row.key}</span>
                <span className="font-semibold flex items-center gap-1">
                  {row.count} müşteri <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                </span>
              </button>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-4" data-testid="report-ilciro">
        <h3 className="font-display font-semibold text-sm mb-3">İl Hedefleri (tıkla → filtrele)</h3>
        <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-muted-foreground px-2 mb-1">
          <span>İl</span>
          <span className="flex gap-6"><span>Hedef</span><span>Ciro</span></span>
        </div>
        <div className="space-y-1 max-h-56 overflow-y-auto grid-scroll">
          {ilAgg.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">Veri yok.</p>
          ) : (
            ilAgg.map((row) => (
              <button
                key={row.il}
                onClick={() => onFilterClick?.("il", row.il)}
                className="w-full flex items-center justify-between text-sm py-1.5 px-2 rounded hover:bg-secondary transition-colors"
                data-testid={`ilciro-row-${row.il}`}
              >
                <span className="truncate">{row.il}</span>
                <span className="flex gap-6">
                  <span className="font-semibold text-primary" data-testid={`ilhedef-${row.il}`}>₺{fmt(row.hedef)}</span>
                  <span className="font-medium text-emerald-500">₺{fmt(row.ciro)}</span>
                </span>
              </button>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}
